import { useEffect, useMemo } from 'react';

/**
 * Injects a JSON-LD <script> tag in document.head and removes it on unmount.
 *
 * Defensive design (prevents Google Search Console "Duplicate field" errors):
 * - `data` is stringified via useMemo so the effect re-runs only when the
 *   JSON content actually changes (not on every parent re-render).
 * - Before appending, removes ANY existing script with the same id — guards
 *   against React 19 concurrent rendering / Strict Mode race conditions
 *   that could leave a stale sibling in the DOM.
 * - On unmount, removes ALL scripts with the same id (not just one).
 * - Requires `id` for deterministic cleanup.
 *
 * Usage:
 *   <JsonLd id="ld-page-faq" data={{ '@context': 'https://schema.org', ... }} />
 */
const JsonLd = ({ data, id }) => {
  const json = useMemo(() => JSON.stringify(data), [data]);

  if (typeof window === 'undefined' && globalThis.__WPAA_SSR__ && id && json) {
    globalThis.__WPAA_SSR__.jsonld.push({ id, json });
  }

  useEffect(() => {
    if (!id || !json) return undefined;

    // Defensive: remove any pre-existing scripts with this id before appending.
    document
      .querySelectorAll(`script[id="${CSS.escape(id)}"][type="application/ld+json"]`)
      .forEach((el) => el.remove());

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = id;
    script.text = json;
    document.head.appendChild(script);

    return () => {
      document
        .querySelectorAll(`script[id="${CSS.escape(id)}"][type="application/ld+json"]`)
        .forEach((el) => el.remove());
    };
  }, [json, id]);

  return null;
};

export default JsonLd;
